import queryString from 'query-string';
import isBoolean from 'lodash/isBoolean';
import isObject from 'lodash/isObject';
import toLower from 'lodash/toLower';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import { matchPath } from 'react-router';
import { LocationDescriptor, LocationDescriptorObject } from 'history';

import { history } from '../../configureStore';

import Base64 from './base64';
import {
    ALL_LANGUAGES,
    URL_FOREIGN_LANGUAGE_ROUTE,
    URL_LANGUAGE_LEARNING_ROUTE,
    URL_LOTE_ROUTE
} from '../../shop/language/constants';
import {
    URL_LIBX_ROUTE,
    URL_SUFFIX_LIBX_GENRES,
    URL_SUFFIX_LIBX_LOCAL_REGIONS
} from '../../yourborrowbox/libx/constants';
import { URL_ALLPRODUCTS_ROUTE, URL_FEATURED_ROUTE } from '../../shop/language/lote/constants';
import { CatalogueLocationDescriptorParams } from '../utils';

export interface TopHitLocationDescriptorParams {
    filter: object | undefined;
    scope: string | null | undefined;
    topHitLabel?: string;
    basePath?: string;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface GenrePageLocationDescriptorParams {
    loanFormat: string | null | undefined;
    genreId: string;
    subGenreId?: string | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface CampaignDescriptorParams {
    loanFormat: string | null | undefined;
    filter: object | string | null | undefined;
    sortBy?: string | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface LanguageLearningDescriptorParams {
    language: string | undefined | null;
    loanFormat: string | undefined | null;
    filter: object | string | null | undefined;
    sortBy?: string | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface LanguageLearningLinkParams {
    language: string | undefined | null;
    loanFormat: string | undefined | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface LanguageLearningAllProductsParams {
    languageId: string | undefined;
    loanFormat: string;
    isMultiLanguageShopEnabled: boolean;
    shopLanguage: string | undefined;
    ageCategory: string | undefined;
}

interface LanguageLearningGenreDescriptorStringParams {
    targetLanguage: string | number | boolean;
    loanFormat: string | undefined | null;
    isMultiLanguageShopEnabled: boolean;
    shopLanguage: string | undefined;
    ageCategory: string | undefined;
}

interface ForeignLanguageDescriptorParams {
    language: string | undefined | null;
    loanFormat: string | undefined | null;
    filter: object | string | null | undefined;
    sortBy?: string | null;
    ageCategory: string | undefined;
}

export function modifyURLState(oldSearch, stateName, changedState, encode = true) {
    const oldQuery = queryString.parse(oldSearch);
    const queryParam = {};

    queryParam[stateName] = encode
        ? Base64.encode(JSON.stringify(changedState))
        : isBoolean(changedState)
        ? changedState
            ? null
            : undefined
        : changedState;
    return queryString.stringify({ ...oldQuery, ...queryParam }) || '';
}

export type UrlParam = {
    readonly name: string;
    readonly value?: string | number | boolean | null | object;
    readonly encode: boolean;
};

export function modifyURLStates(oldSearch: string, states: ReadonlyArray<UrlParam>) {
    const oldQuery = queryString.parse(oldSearch);
    const queryParam = {};

    for (const state of states) {
        queryParam[state.name] = state.encode
            ? Base64.encode(JSON.stringify(state.value))
            : isBoolean(state.value)
            ? state.value
                ? null
                : undefined
            : state.value;
    }
    return queryString.stringify({ ...oldQuery, ...queryParam }) || '';
}

type LocationDescriptorObjectWithQuery = LocationDescriptorObject & { readonly query?: { readonly filter: string } };

export default class RoutingUtils {
    static getQueryParameterByName(name, url) {
        if (!url) url = window.location.href;
        // eslint-disable-next-line no-useless-escape
        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    static stringifyQuery(query) {
        return queryString.stringify(query);
    }

    static replaceURLSearchState(stateName, changedState, encode = true) {
        const search = modifyURLState(history.location.search, stateName, changedState, encode);
        history.replace({
            pathname: history.location.pathname,
            search
        });
    }

    static replaceURLSearchStates(states: ReadonlyArray<UrlParam>, pathname: string = history.location.pathname) {
        const search = modifyURLStates(history.location.search, states);
        history.replace({
            pathname,
            search
        });
    }

    static pushProductId(productId) {
        RoutingUtils.pushUrl(`/products/${productId}`);
    }

    static pushUrl(path) {
        history.push(path);
    }

    static pushUrlWithQuery(path, params) {
        const search = queryString.stringify(params);
        const fullQuery = {
            pathname: path,
            search
        };
        history.push(fullQuery);
    }

    static buildUrl(paths) {
        //TODO check if ...paths is needed
        if (typeof paths[0] === 'object') {
            paths = paths[0];
        }
        return `/${compact(flatten(paths)).join('/')}`;
    }

    static searchLocationDescriptor(
        searchTerm: string | undefined,
        searchScope: string | undefined,
        pathname: string = '/search'
    ): LocationDescriptor {
        const location: LocationDescriptorObject = { pathname };

        if (searchTerm) {
            location.search = queryString.stringify({ q: searchTerm, scope: searchScope });
        }
        return location;
    }

    static topHitLocationDescriptor({
        filter,
        scope,
        topHitLabel = '',
        basePath = '/search',
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: TopHitLocationDescriptorParams): LocationDescriptor {
        const searchRelatedQuery = {};
        if (filter) {
            searchRelatedQuery['filter'] = Base64.encode(JSON.stringify(filter));
        }
        if (scope) {
            searchRelatedQuery['scope'] = scope;
        }
        if (topHitLabel) {
            searchRelatedQuery['searchLabel'] = encodeURIComponent(topHitLabel);
        }

        if (isMultiLanguageShopEnabled) {
            searchRelatedQuery['shopLanguage'] = shopLanguage;
        }

        if (ageCategory) {
            searchRelatedQuery['ageCategory'] = ageCategory;
        }

        return `${basePath}?${queryString.stringify(searchRelatedQuery)}`;
    }

    static catalogueLocationDescriptor({
        loanFormat,
        filter = null,
        isMultiLanguageShopEnabled,
        shopLanguage = 'all',
        ageCategory
    }: CatalogueLocationDescriptorParams): LocationDescriptorObjectWithQuery {
        const location: LocationDescriptorObjectWithQuery = {
            // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
            //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
            pathname: isMultiLanguageShopEnabled
                ? `/shop/${shopLanguage}/${loanFormat ? toLower(loanFormat) : 'all'}/products`
                : `/catalogue/${loanFormat ? toLower(loanFormat) : 'all'}/products`,
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
        if (filter !== null) {
            // TODO: check if filter is always a object (must be)
            // add error handling if filter argument is not an object in future
            const base64EncodedFilter = Base64.encode(JSON.stringify(filter));
            const encodedFilter = isObject(filter) ? encodeURIComponent(base64EncodedFilter) : base64EncodedFilter;
            return {
                ...location,
                query: { filter: encodedFilter }
            };
        }
        return location;
    }

    static campaignsLocationDescriptor({
        loanFormat,
        filter = null,
        isMultiLanguageShopEnabled,
        shopLanguage = 'all',
        ageCategory
    }: CampaignDescriptorParams): LocationDescriptorObjectWithQuery {
        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}/unlimited` : 'unlimited';

        const location: LocationDescriptorObjectWithQuery = {
            pathname: this.buildUrl([path, 'campaigns', loanFormat ? toLower(loanFormat) : 'all']),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
        if (filter !== null) {
            // TODO: check if filter is always a object (must be)
            // add error handling if filter argument is not an object in future
            const base64EncodedFilter = Base64.encode(JSON.stringify(filter));
            const encodedFilter = isObject(filter) ? encodeURIComponent(base64EncodedFilter) : base64EncodedFilter;
            return {
                ...location,
                query: { filter: encodedFilter }
            };
        }

        return location;
    }

    static libxLocationDescriptor({
        filter = null,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        filter: object | string | null;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
        ageCategory: string | undefined;
    }): LocationDescriptorObjectWithQuery {
        const location: LocationDescriptorObjectWithQuery = {
            pathname: isMultiLanguageShopEnabled
                ? `/shop/${shopLanguage}/${URL_LIBX_ROUTE}/products`
                : `/${URL_LIBX_ROUTE}/products`,
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
        if (filter !== null) {
            const base64EncodedFilter = Base64.encode(JSON.stringify(filter));
            const encodedFilter = isObject(filter) ? encodeURIComponent(base64EncodedFilter) : base64EncodedFilter;
            return {
                ...location,
                query: { filter: encodedFilter }
            };
        }
        return location;
    }

    static catalogueLocationDescriptorString({
        loanFormat,
        filter,
        sortBy,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: CatalogueLocationDescriptorParams): string {
        const location = this.catalogueLocationDescriptor({
            loanFormat,
            filter,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory
        });

        const locationFilter = location.query?.filter;
        const params = [
            {
                key: 'filter',
                value: locationFilter
            },
            {
                key: 'sortBy',
                value: sortBy
            },
            {
                key: 'ageCategory',
                value: ageCategory
            }
        ];

        return this.urlWithQueryParamsToString(location.pathname, params);
    }

    static campaignsLocationDescriptorString({
        loanFormat,
        filter = null,
        sortBy = null,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: CampaignDescriptorParams): string {
        const location = this.campaignsLocationDescriptor({
            loanFormat,
            filter,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory
        });
        const locationFilter = location.query?.filter;
        const params = [
            {
                key: 'filter',
                value: locationFilter
            },
            {
                key: 'sortBy',
                value: sortBy
            },
            {
                key: 'ageCategory',
                value: ageCategory
            }
        ];

        return this.urlWithQueryParamsToString(location.pathname, params);
    }

    static libxLocationDescriptorString({
        filter = null,
        sortBy = null,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        filter: object | string | null | undefined;
        sortBy?: string | null;
        ageCategory: string | undefined;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
    }): string {
        const location = this.libxLocationDescriptor({ filter, isMultiLanguageShopEnabled, shopLanguage, ageCategory });
        const params = [
            {
                key: 'filter',
                value: location.query?.filter
            },
            {
                key: 'sortBy',
                value: sortBy
            },
            {
                key: 'ageCategory',
                value: ageCategory
            }
        ];
        return this.urlWithQueryParamsToString(location.pathname, params);
    }

    static genrePageLocationDescriptor({
        loanFormat,
        genreId,
        subGenreId,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: GenrePageLocationDescriptorParams): LocationDescriptorObject {
        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : 'catalogue';
        return {
            pathname: this.buildUrl([
                path,
                // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
                //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
                toLower(loanFormat ?? 'all'),
                'genres',
                encodeURIComponent(genreId),
                subGenreId ? encodeURIComponent(subGenreId.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static genrePageForeignLanguageLocationDescriptor({
        loanFormat,
        language,
        genreId,
        ageCategory,
        subGenreId
    }: Pick<GenrePageLocationDescriptorParams, 'genreId' | 'subGenreId' | 'ageCategory' | 'loanFormat'> & {
        language: string;
    }): LocationDescriptorObject {
        return {
            pathname: this.buildUrl([
                'language/lote',
                encodeURIComponent(language),
                'genres',
                toLower(loanFormat ?? 'all'),
                encodeURIComponent(genreId),
                subGenreId ? encodeURIComponent(subGenreId.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static publisherPageLocationDescriptor({
        loanFormat,
        publisher,
        imprint,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        loanFormat?: string | null;
        publisher?: string | null;
        imprint?: string | null;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
        ageCategory: string | undefined;
    }): LocationDescriptorObject {
        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : 'catalogue';

        return {
            pathname: this.buildUrl([
                path,
                // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
                //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
                toLower(loanFormat ?? 'all'),
                'publishers',
                publisher ? encodeURIComponent(publisher) : undefined,
                imprint ? encodeURIComponent(imprint.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static libxGenrePageLocationDescriptor({
        genreId,
        subGenreId,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        genreId: string;
        subGenreId?: string | null;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
        ageCategory: string | undefined;
    }): LocationDescriptorObject {
        const libxPath = isMultiLanguageShopEnabled ? `shop/${shopLanguage}/${URL_LIBX_ROUTE}` : URL_LIBX_ROUTE;

        return {
            pathname: this.buildUrl([
                libxPath,
                URL_SUFFIX_LIBX_GENRES,
                encodeURIComponent(genreId),
                subGenreId ? encodeURIComponent(subGenreId.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static libXLocalRegionPageLocationDescriptor({
        level1,
        level2,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        level1: string;
        level2?: string | null;
        shopLanguage?: string | undefined;
        isMultiLanguageShopEnabled?: boolean;
        ageCategory: string | undefined;
    }): LocationDescriptorObject {
        const libxLocalRegionPath = isMultiLanguageShopEnabled
            ? `shop/${shopLanguage}/${URL_LIBX_ROUTE}`
            : URL_LIBX_ROUTE;
        return {
            pathname: this.buildUrl([
                libxLocalRegionPath,
                URL_SUFFIX_LIBX_LOCAL_REGIONS,
                encodeURIComponent(level1),
                level2 ? encodeURIComponent(level2.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static languageLearningDescriptor({
        language,
        loanFormat,
        filter = null,
        isMultiLanguageShopEnabled,
        shopLanguage,
        ageCategory
    }: LanguageLearningDescriptorParams): LocationDescriptorObjectWithQuery {
        const path = this.languageLearningLanguageLink({
            language,
            loanFormat,
            shopLanguage,
            isMultiLanguageShopEnabled,
            ageCategory
        });
        const location: LocationDescriptorObjectWithQuery = {
            pathname: path ?? undefined,
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
        if (filter !== null) {
            const base64EncodedFilter = Base64.encode(JSON.stringify(filter));
            const encodedFilter = isObject(filter) ? encodeURIComponent(base64EncodedFilter) : base64EncodedFilter;
            return {
                ...location,
                query: { filter: encodedFilter }
            };
        }
        return location;
    }

    static languageLearningGenreDescriptorString({
        targetLanguage,
        loanFormat,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled
    }: LanguageLearningGenreDescriptorStringParams) {
        const languageLearningPath = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : URL_FOREIGN_LANGUAGE_ROUTE;
        return `/${languageLearningPath}/${URL_LANGUAGE_LEARNING_ROUTE}/${URL_FEATURED_ROUTE}/${encodeURIComponent(
            targetLanguage
        )}/${encodeURIComponent(toLower(loanFormat))}`;
    }

    static languageLearningDescriptorString({
        language,
        loanFormat,
        filter = null,
        sortBy = null,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: LanguageLearningDescriptorParams): string {
        const location = this.languageLearningDescriptor({
            language,
            loanFormat,
            filter,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory
        });
        const params = [
            {
                key: 'filter',
                value: location.query?.filter
            },
            {
                key: 'sortBy',
                value: sortBy
            },
            {
                key: 'ageCategory',
                value: ageCategory
            }
        ];
        return this.urlWithQueryParamsToString(location.pathname, params);
    }

    static foreignLanguageDescriptor(
        language: string | undefined | null,
        loanFormat: string | undefined | null,
        filter: object | string | null = null,
        ageCategory: string | undefined
    ): LocationDescriptorObjectWithQuery {
        const path = this.loteLanguageLink({ language, loanFormat });
        const location: LocationDescriptorObjectWithQuery = {
            pathname: path ?? undefined,
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
        if (filter !== null) {
            const base64EncodedFilter = Base64.encode(JSON.stringify(filter));
            const encodedFilter = isObject(filter) ? encodeURIComponent(base64EncodedFilter) : base64EncodedFilter;
            return {
                ...location,
                query: { filter: encodedFilter }
            };
        }
        return location;
    }

    static foreignLanguageDescriptorString({
        language,
        loanFormat,
        filter = null,
        sortBy = null,
        ageCategory
    }: ForeignLanguageDescriptorParams): string {
        const location = this.foreignLanguageDescriptor(language, loanFormat, filter, ageCategory);
        const params = [
            {
                key: 'filter',
                value: location.query?.filter
            },
            {
                key: 'sortBy',
                value: sortBy
            },
            {
                key: 'ageCategory',
                value: ageCategory
            }
        ];
        return this.urlWithQueryParamsToString(location.pathname, params);
    }

    static languageLearningLanguageLink({
        language,
        loanFormat,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled
    }: LanguageLearningLinkParams): string | null {
        if (!language) {
            return null;
        }

        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : URL_FOREIGN_LANGUAGE_ROUTE;

        return this.buildUrl([
            path,
            URL_LANGUAGE_LEARNING_ROUTE,
            encodeURIComponent(language),
            URL_ALLPRODUCTS_ROUTE,
            loanFormat ? toLower(loanFormat) : 'all'
        ]);
    }

    static loteLanguageLink({
        language,
        loanFormat
    }: {
        language: string | undefined | null;
        loanFormat: string | undefined | null;
    }): string | null {
        if (!language) {
            return null;
        }

        return this.buildUrl([
            URL_FOREIGN_LANGUAGE_ROUTE,
            URL_LOTE_ROUTE,
            encodeURIComponent(language),
            URL_ALLPRODUCTS_ROUTE,
            loanFormat ? toLower(loanFormat) : 'all'
        ]);
    }

    static foreignLanguageAllProductsPageLocationDescriptor({
        loanFormat,
        languageId,
        ageCategory
    }: {
        languageId: string | null | undefined;
        loanFormat: string;
        ageCategory: string | undefined;
    }): LocationDescriptorObject {
        return {
            pathname: this.buildUrl([
                URL_FOREIGN_LANGUAGE_ROUTE,
                URL_LOTE_ROUTE,
                encodeURIComponent(languageId ?? ALL_LANGUAGES),
                URL_ALLPRODUCTS_ROUTE,
                loanFormat
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static languageLearningAllProductsPageLocationDescriptor({
        isMultiLanguageShopEnabled,
        shopLanguage,
        languageId,
        ageCategory,
        loanFormat
    }: LanguageLearningAllProductsParams): LocationDescriptorObject {
        if (isMultiLanguageShopEnabled) {
            return {
                pathname: this.buildUrl([
                    'shop',
                    shopLanguage,
                    URL_LANGUAGE_LEARNING_ROUTE,
                    encodeURIComponent(languageId ?? ALL_LANGUAGES),
                    URL_ALLPRODUCTS_ROUTE,
                    loanFormat
                ]),
                search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
            };
        }

        return {
            pathname: this.buildUrl([
                URL_FOREIGN_LANGUAGE_ROUTE,
                URL_LANGUAGE_LEARNING_ROUTE,
                encodeURIComponent(languageId ?? ALL_LANGUAGES),
                URL_ALLPRODUCTS_ROUTE,
                loanFormat
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static urlWithQueryParamsToString(
        baseUrl: string | undefined,
        queryParams: ReadonlyArray<{ readonly key: string; readonly value?: string | null }> = []
    ): string {
        return (
            (baseUrl ?? '') +
            (queryParams.filter(o => o.value ?? '').length > 0
                ? `?${queryParams
                      .filter(o => o.value)
                      .map(o => `${o.key}=${o.value}`)
                      .join('&')}`
                : '')
        );
    }

    static createNonEncodedPathParam(stateName: string, stateValue: UrlParam['value']): UrlParam {
        return {
            name: stateName,
            value: stateValue,
            encode: false
        };
    }

    static getForeignLanguageUrlWithReplacedLanguage(
        language: string,
        pathname: string,
        isMultiLanguageShopEnabled: boolean
    ): string | null {
        if (isMultiLanguageShopEnabled) {
            const match = matchPath<{ shopLanguage: string; section: string; page: string; loanFormat: string }>(
                pathname,
                '/shop/:shopLanguage/languagelearning/:languageId/:page/:loanFormat'
            );

            if (!match) {
                return null;
            }

            const { shopLanguage, page, loanFormat } = match.params;

            return `/shop/${shopLanguage}/languagelearning/${language}/${page}/${loanFormat}`;
        }

        const match = matchPath<{ section: string; page: string; loanFormat: string }>(
            pathname,
            '/language/:section/:languageId/:page/:loanFormat'
        );

        if (!match) {
            return null;
        }

        const { section, page, loanFormat } = match.params;

        return `/language/${section}/${language}/${page}/${loanFormat}`;
    }
}
